import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'

interface CalculatorValues {
  buyPrice?: string
  sellPrice?: string
  marketplaceFee?: string
  collectionFee?: string
  ethUsdRate?: string
  walletAddress?: string
}

/**
 * Calculator Store
 * Manages calculator state and persistence
 */
export const useCalculatorStore = defineStore('calculator', () => {
  // State
  const values = ref<CalculatorValues>({})

  /**
   * Sets a cookie with the given name and value
   */
  const setCookie = (name: string, value: any, days: number = 30): void => {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};${expires};path=/;SameSite=Lax`
  }

  /**
   * Gets a cookie value by name
   */
  const getCookie = (name: string): any | null => {
    const nameEQ = `${name}=`
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) {
        try {
          return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)))
        } catch (e) {
          return null
        }
      }
    }
    return null
  }

  /**
   * Update calculator values
   */
  const updateValues = (newValues: CalculatorValues) => {
    values.value = { ...values.value, ...newValues }
    setCookie('calculatorValues', values.value)
  }

  /**
   * Clear all values
   */
  const clearValues = () => {
    values.value = {}
    document.cookie = 'calculatorValues=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  }

  /**
   * Get saved values from cookies
   */
  const getSavedValues = (): CalculatorValues | null => {
    return getCookie('calculatorValues')
  }

  /**
   * Load saved values into state
   */
  const loadSavedValues = () => {
    const savedValues = getSavedValues()
    if (savedValues) {
      values.value = savedValues
    }
  }

  return {
    values: readonly(values),
    updateValues,
    clearValues,
    getSavedValues,
    loadSavedValues
  }
}) 